// Which of the pharmacy Orders table's columns are visible, and how that choice
// persists. A close copy of ../orders-legacy/columnPrefs.js (the ops console's own
// version of this file), adapted to pharmacy's smaller column set.
//
// STORAGE_KEY is deliberately its OWN string, distinct from ops's
// "pillup.ops.orders.columns" -- these are two different tables on two different
// consoles, and sharing a key would mean choosing columns on one screen silently
// changed what the other shows.
//
// IDs BELOW ARE REACT-TABLE COLUMN IDs, NOT HEADERS -- see the note in the ops version
// of this file for why. Keep these in step with src/components/orderTable/Constants.js
// and ./columns.jsx.
//
// The `#` column is excluded for the same reason it is in the ops version: its
// accessor is `""`, so it has no usable id, and it stays permanently visible.

/** Visible when the user has never chosen. */
export const DEFAULT_VISIBLE = [
    "id",
    "mapUserPatient.Patient.name",
    "status",
    "contact",
    "startDate",
    "endDate",
    "daysLeft",
    "total",
    "type",
    "quotationId",
    "salesPerson",
    "action",
];

/**
 * Every hideable column, grouped for the picker. Order within a group is the order the
 * checkboxes render in; group order is the order the sections render in.
 */
export const COLUMN_GROUPS = [
    {
        label: "Identity",
        columns: [
            { id: "id", label: "ID" },
            { id: "mapUserPatient.Patient.name", label: "Name" },
            // Same reasoning as the ops version: off by default because it repeats
            // Name whenever the order's relationship is SELF.
            { id: "caregiverUser", label: "Caregiver" },
            { id: "contact", label: "Contact" },
            { id: "quotationId", label: "Quotation Id" },
        ],
    },
    {
        label: "Status & fulfilment",
        columns: [
            { id: "status", label: "Status" },
            { id: "type", label: "Type" },
            { id: "channel", label: "Channel" },
            { id: "source", label: "Source" },
            { id: "salesPerson", label: "Sales Person" },
            { id: "action", label: "Action" },
        ],
    },
    {
        label: "Dates",
        columns: [
            { id: "startDate", label: "Start Date" },
            { id: "endDate", label: "End Date" },
            { id: "noOfDays", label: "Duration" },
            { id: "daysLeft", label: "Days Left" },
        ],
    },
    {
        label: "Money",
        columns: [
            { id: "total", label: "Total Amount (₹)" },
            { id: "evitalMedicineAmount", label: "Evital Med Amt (₹)" },
            { id: "serviceChargeAmount", label: "Service Charge (₹)" },
            { id: "inventoryId", label: "Inventory No" },
        ],
    },
    {
        label: "Address",
        columns: [
            { id: "city", label: "Order City" },
            { id: "address.state", label: "State" },
        ],
    },
];

/** Every hideable id, flattened -- the universe `hiddenColumns` is computed against. */
export const ALL_HIDEABLE_IDS = COLUMN_GROUPS.flatMap((group) => group.columns.map((column) => column.id));

// Bump when DEFAULT_VISIBLE changes -- see the ops version of this file for why.
const STORAGE_KEY = "pillup.pharmacy.orders.columns";
const STORAGE_VERSION = 2;

/** Ids hidden under the default -- i.e. everything hideable that isn't in DEFAULT_VISIBLE. */
export function defaultHiddenColumns() {
    return ALL_HIDEABLE_IDS.filter((id) => !DEFAULT_VISIBLE.includes(id));
}

/**
 * The user's stored hidden-column ids, or the default set.
 *
 * Never throws -- localStorage can be unavailable or the stored value malformed, and a
 * column preference is not worth failing a page render over.
 */
export function loadHiddenColumns() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultHiddenColumns();
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.hidden)) {
            return defaultHiddenColumns();
        }
        return parsed.hidden.filter((id) => ALL_HIDEABLE_IDS.includes(id));
    } catch (error) {
        return defaultHiddenColumns();
    }
}

/** Persist the hidden-column ids. Silently no-ops if storage is unavailable. */
export function saveHiddenColumns(hidden) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, hidden }));
    } catch (error) {
        // Ignored on purpose -- the choice still applies for this session.
    }
}
